/**
 * Edit Role Modal - Extracted from admin.tsx per ADR-039
 * Modal for editing existing custom roles and their permissions
 */

import type { PermissionResponse, RoleResponse } from "@/types/models"
import {
  Box,
  Flex,
  Input,
  Stack,
  Text,
  Textarea,
  createToaster,
} from "@chakra-ui/react"
import React, { useState } from "react"

import { Button } from "@/components/atoms/Button"
import { Checkbox } from "@/components/atoms/Checkbox"
import {
  DialogBackdrop,
  DialogBody,
  DialogCloseTrigger,
  DialogContent,
  DialogHeader,
  DialogPositioner,
  DialogRoot,
  DialogTitle,
  Portal,
} from "@chakra-ui/react"

interface RoleUpdateData {
  name?: string
  display_name?: string
  description?: string
  permission_ids?: string[]
}

export function EditRoleModal({
  isOpen,
  onClose,
  role,
  permissions,
  onUpdateRole,
}: {
  isOpen: boolean
  onClose: () => void
  role: RoleResponse | null
  permissions: PermissionResponse[]
  onUpdateRole: (data: { roleId: string; roleData: RoleUpdateData }) => void
}) {
  const [formData, setFormData] = useState({
    name: "",
    display_name: "",
    description: "",
    permission_ids: [] as string[],
  })
  const toaster = createToaster({
    placement: "top-end",
  })

  // Initialize form data when role changes
  React.useEffect(() => {
    if (role) {
      setFormData({
        name: role.name || "",
        display_name: role.display_name || "",
        description: role.description || "",
        permission_ids: role.permissions?.map((p: PermissionResponse) => p.id) || [],
      })
    }
  }, [role])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!role) return
    try {
      await onUpdateRole({
        roleId: role.id,
        roleData: formData,
      })
      toaster.create({
        title: "Role updated successfully",
        type: "success",
        duration: 3000,
      })
      onClose()
    } catch (error) {
      toaster.create({
        title: "Failed to update role",
        type: "error",
        duration: 3000,
      })
    }
  }

  const groupedPermissions = (permissions || []).reduce(
    (
      acc: Record<string, PermissionResponse[]>,
      permission: PermissionResponse,
    ) => {
      const resourceType = permission.resource_type || "general"
      if (!acc[resourceType]) {
        acc[resourceType] = []
      }
      acc[resourceType].push(permission)
      return acc
    },
    {},
  )

  if (!role) return null

  return (
    <DialogRoot
      open={isOpen}
      onOpenChange={(e) => !e.open && onClose()}
      size={{ base: "full", md: "xl" }}
    >
      <Portal>
        <DialogBackdrop />
        <DialogPositioner
          inset="0"
          display="grid"
          placeItems="center"
          padding={{ base: 4, md: 6 }}
        >
          <DialogContent>
            <DialogHeader>
              <DialogTitle>
                Edit Role: {role.display_name || role.name}
              </DialogTitle>
              <DialogCloseTrigger />
            </DialogHeader>
            <DialogBody pb={6}>
              <form onSubmit={handleSubmit}>
                <Stack gap={4} align="stretch">
                  <Flex>
                    <Box>
                      <Text fontWeight="medium" mb={2}>
                        Role Name
                      </Text>
                      <Input
                        value={formData.name}
                        onChange={(e) =>
                          setFormData((prev) => ({
                            ...prev,
                            name: e.target.value,
                          }))
                        }
                        placeholder="e.g. content-moderator"
                        required
                      />
                    </Box>
                    <Box>
                      <Text fontWeight="medium" mb={2}>
                        Display Name
                      </Text>
                      <Input
                        value={formData.display_name}
                        onChange={(e) =>
                          setFormData((prev) => ({
                            ...prev,
                            display_name: e.target.value,
                          }))
                        }
                        placeholder="e.g. Content Moderator"
                        required
                      />
                    </Box>
                  </Flex>

                  <Box>
                    <Text fontWeight="medium" mb={2}>
                      Description
                    </Text>
                    <Textarea
                      value={formData.description}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          description: e.target.value,
                        }))
                      }
                      placeholder="Describe what this role can do..."
                    />
                  </Box>

                  <Box>
                    <Text fontWeight="medium" mb={2}>
                      Permissions
                    </Text>
                    <Box
                      maxH="300px"
                      overflowY="auto"
                      border="1px"
                      borderColor="gray.200"
                      borderRadius="md"
                      p={4}
                    >
                      {Object.entries(groupedPermissions).map(
                        ([resourceType, resourcePermissions]: [
                          string,
                          PermissionResponse[],
                        ]) => (
                          <Box key={resourceType} mb={4}>
                            <Text
                              fontWeight="medium"
                              mb={2}
                              textTransform="capitalize"
                            >
                              {resourceType} ({resourcePermissions.length})
                            </Text>
                            <Stack align="start" gap={2} ml={4}>
                              {resourcePermissions.map(
                                (permission: PermissionResponse) => (
                                  <Checkbox
                                    key={permission.id}
                                    checked={formData.permission_ids.includes(
                                      permission.id,
                                    )}
                                    onCheckedChange={(details) => {
                                      const isChecked = details.checked
                                      if (isChecked) {
                                        setFormData((prev) => ({
                                          ...prev,
                                          permission_ids: [
                                            ...prev.permission_ids,
                                            permission.id,
                                          ],
                                        }))
                                      } else {
                                        setFormData((prev) => ({
                                          ...prev,
                                          permission_ids:
                                            prev.permission_ids.filter(
                                              (id) => id !== permission.id,
                                            ),
                                        }))
                                      }
                                    }}
                                  >
                                    <Stack align="start" gap={1}>
                                      <Text fontSize="sm">
                                        {permission.name}
                                      </Text>
                                      <Text fontSize="xs" color="gray.500">
                                        {permission.description}
                                      </Text>
                                    </Stack>
                                  </Checkbox>
                                ),
                              )}
                            </Stack>
                          </Box>
                        ),
                      )}
                    </Box>
                  </Box>

                  <Flex justify="end">
                    <Button variant="outline" onClick={onClose}>
                      Cancel
                    </Button>
                    <Button type="submit" colorScheme="blue">
                      Update Role
                    </Button>
                  </Flex>
                </Stack>
              </form>
            </DialogBody>
          </DialogContent>
        </DialogPositioner>
      </Portal>
    </DialogRoot>
  )
}
